import { useMemo, useState, useEffect } from 'react';
/**
 * Simple shuffle algorithm
 */
import { i18n as _i18n } from "@lingui/core";
const shuffleArray = array => {
  return [...array].sort(() => Math.random() - 0.5);
};

/**
 * Custom hook to generate reviews with hydration-safe randomization
 * @param {boolean} useRandomReviews - Whether to use random reviews
 * @param {number} randomReviewsCount - Number of random reviews to generate
 * @returns {Array} Array of review objects
 */
export function useReviews(useRandomReviews, randomReviewsCount) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [randomIndexes, setRandomIndexes] = useState(null);
  useEffect(() => {
    setIsHydrated(true);

    // Generate random indexes once after hydration
    if (useRandomReviews) {
      const allIndexes = Array.from({
        length: 20
      }, (_, i) => i + 1);
      const shuffled = shuffleArray(allIndexes);
      setRandomIndexes(shuffled.slice(0, randomReviewsCount));
    }
  }, [useRandomReviews, randomReviewsCount]);
  return useMemo(() => {
    const createReview = (index, id) => ({
      id,
      name: _i18n._(
      /*i18n*/
      {
        id: `carlead.ratings.reviews.google.reviewer${index}.name`
      }),
      subtitle: _i18n._(
      /*i18n*/
      {
        id: `carlead.ratings.reviews.google.reviewer${index}.subtitle`
      }),
      rating: 5,
      text: _i18n._(
      /*i18n*/
      {
        id: `carlead.ratings.reviews.google.reviewer${index}.text`
      }),
      visitDate: _i18n._(
      /*i18n*/
      {
        id: `carlead.ratings.reviews.google.reviewer${index}.visit_date`
      }),
      avatar: _i18n._(
      /*i18n*/
      {
        id: `carlead.ratings.reviews.google.reviewer${index}.avatar`
      })
    });

    // Default reviews (always consistent)
    if (!useRandomReviews) {
      return [1, 2, 3].map((index, i) => createReview(index, i + 1));
    }

    // During SSR or before hydration: return fixed order to prevent hydration mismatch
    if (!isHydrated || !randomIndexes) {
      return [1, 2, 3, 4, 5].slice(0, randomReviewsCount).map((index, i) => createReview(index, i + 1));
    }

    // After hydration: use the randomized indexes (generated once)
    return randomIndexes.map((index, i) => createReview(index, i + 1));
  }, [useRandomReviews, randomReviewsCount, isHydrated, randomIndexes]);
}